import React, {
  forwardRef,
  ForwardRefRenderFunction,
  InputHTMLAttributes,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { clamp } from '../../../helpers/math.utils';
import { generateUUID } from '../../../helpers/uuid.utils';
import { Chip } from '../../chip/chip-component';
import styles from './chip-holder-component.module.scss';

type ChipHolderProps = {
  label?: string;
  name?: string;
  helpText?: string;

  /** Called when a chip is added or removed */
  onChipUpdateCallback?: (validChips: string[], invalidChips: string[]) => void;
  /** Checks if an inputted chip is valid or not */
  onChipValidationCallback?: (val: string) => boolean;

  background?: 'white' | 'grey';
  disabled?: boolean;
  chips?: { id: string; text: string; valid: boolean }[];
  errorMessage?: JSX.Element | string;
} & InputHTMLAttributes<HTMLInputElement>;

export interface ChipHolderRef {
  focus(): void;
  blur(): void;
  checkValidity(): boolean;
}

/**
 * Chip Holder component
 *
 * https://www.figma.com/file/7iCQXjofQ7NoGkgmIzKQS4/Basket-actions-%2B-share?type=design&node-id=34-9779&mode=design&t=1wo6fLGNRa4jQ1Jq-0
 */
const ChipHolderRefForwardingComponent: ForwardRefRenderFunction<ChipHolderRef, ChipHolderProps> = (props: ChipHolderProps, ref) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const {
    label,
    name,
    helpText,
    onChipUpdateCallback,
    onChipValidationCallback,
    background,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    className,
    onBlur,
    disabled,
    placeholder,
    chips,
    ...inputAttributes
  } = props;

  // exposes internal input methods via ref
  useImperativeHandle(ref, () => ({
    focus: () => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    },

    blur: () => {
      if (inputRef.current) {
        inputRef.current.blur();
      }
    },

    checkValidity: (): boolean => {
      if (inputRef.current) {
        return inputRef.current.checkValidity();
      }

      return false;
    },
  }));

  const [lastChipsProp, setLastChipsProp] = useState<
    | {
        id: string;
        text: string;
        valid: boolean;
      }[]
    | undefined
  >(chips);
  const [currentChips, setCurrentChips] = useState<{ id: string; text: string; valid: boolean }[]>(chips ?? []);
  const [focused, setFocused] = useState(false); // Whether the input is focused or not. Used to hide it when not focused

  if (chips !== lastChipsProp) {
    if (chips) setCurrentChips(chips);
    setLastChipsProp(chips);
  }

  const [inputValue, setInputValue] = useState('');
  const [isValidState, setIsValidState] = useState(true);

  const chipsRef = useRef<Array<HTMLDivElement | null>>([]); // Refs to all the chip components

  const [selectedChipIndex, setSelectedChipIndex] = useState(-1); // The index of the currently selected chip

  const handleChipsUpdate = useCallback(
    (updatedChips: { id: string; text: string; valid: boolean }[]) => {
      setIsValidState(updatedChips.every((c) => c.valid));
      if (onChipUpdateCallback) {
        const validChips = updatedChips.filter((c) => c.valid).map((c) => c.text);
        const invalidChips = updatedChips.filter((c) => !c.valid).map((c) => c.text);
        onChipUpdateCallback?.(validChips, invalidChips);
      }
    },
    [onChipUpdateCallback]
  );

  const createChips = useCallback(
    (inputVal: string): boolean => {
      const chipTexts = inputVal.split(',');

      let createdChip = false;
      const newChips = [];
      for (let text of chipTexts) {
        text = text.trim();
        if (text === '') continue;

        const valid = onChipValidationCallback ? onChipValidationCallback(text) : true;
        newChips.push({ id: generateUUID(), text: text, valid: valid });
        createdChip = true;
      }

      setInputValue('');

      if (!createdChip) return false;

      const updatedChips = [...currentChips, ...newChips];

      setCurrentChips(updatedChips);
      handleChipsUpdate(updatedChips);

      return true;
    },
    [currentChips, handleChipsUpdate, onChipValidationCallback]
  );

  useEffect(() => {
    chipsRef.current = chipsRef.current.slice(0, currentChips.length);
  }, [currentChips]);

  return (
    <div
      className={`${styles['chip-holder-component']} ${disabled ? styles.disabled : ''}`}
      onClick={() => {
        inputRef.current?.focus();
      }}
      onKeyDown={handleKeyDown}
      data-testid='chip-holder'
    >
      {label ? <label htmlFor={name}>{label}</label> : null}

      {helpText ? <p>{helpText}</p> : null}

      <div className={`${styles['input-container']} ${styles[background ?? 'grey']}`}>
        {/* An extra div is needed to remove the bottom margin added by the flex-gap-col mixin */}
        <div className={`${styles['chip-holder']}`} style={{ '--chip-height': '3.2rem' } as React.CSSProperties}>
          {currentChips.map(({ id, text, valid }, i) => (
            <Chip
              ref={(el) => (chipsRef.current[i] = el)}
              activeOnFocus
              key={id}
              text={text}
              icon='close'
              onIconClickCallback={() => handleDeleteChip(id)}
              isValid={valid}
              disabled={disabled}
            />
          ))}
          <input
            data-testid='chip-holder-input'
            onBlur={onFocusLost}
            onFocus={() => {
              setFocused(true);
              setSelectedChipIndex(-1);
            }}
            type='text'
            className={`${styles.text} ${background ? styles[background] : styles.grey} ${
              focused || inputValue !== '' ? '' : styles.hidden
            }`}
            {...inputAttributes}
            ref={inputRef}
            onKeyDown={handleInputKeyDown}
            onChange={(e) => {
              const val = e.target.value;
              setInputValue(val);
              if (val.includes(',')) {
                createChips(val);
              }
            }}
            value={inputValue}
            disabled={disabled}
            name={name}
            placeholder={currentChips.length === 0 ? placeholder : undefined}
          />
        </div>
      </div>
      {props.errorMessage && !isValidState && <p className={styles['error-text']}>{props.errorMessage}</p>}
    </div>
  );

  function handleInputKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    // Adds a new chip when the `Enter` or `,` keys are pressed
    if (event.key === 'Enter' || event.key === ',' || event.key === 'Tab') {
      if (createChips(inputValue) || event.key !== 'Tab') event.preventDefault();
    }
  }

  // Deletes a chip by UUID
  function handleDeleteChip(uuid: string) {
    const updatedChips = currentChips.filter((chip) => chip.id !== uuid);
    setCurrentChips(updatedChips);
    handleChipsUpdate(updatedChips);
  }

  // Allows the user to use the chip holder using only the keyboard
  function handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    switch (event.key) {
      case 'Escape':
        // Blurs the selected chip
        if (selectedChipIndex !== -1) {
          chipsRef.current[selectedChipIndex]?.blur();
          setSelectedChipIndex(-1);
        }
        break;
      case 'ArrowLeft':
        moveSelectedChip(-1);
        break;
      case 'ArrowRight':
        moveSelectedChip(1);
        break;
      case 'Delete':
        deleteChipAtSelectedIndex();
        break;
      case 'Backspace':
        // When `Backspace` is pressed, if no chip is selected then the last chip is selected,
        // else the selected chip is deleted
        if (event.target === inputRef.current && inputRef.current.value !== '') break;
        if (selectedChipIndex === -1) {
          moveSelectedChip(-1);
        } else {
          deleteChipAtSelectedIndex();
        }
        break;
    }
  }

  function deleteChipAtSelectedIndex() {
    let index = selectedChipIndex;
    // When no chip is selected
    if (index === -1) {
      setSelectedChipIndex(-1);
      return;
    }

    // Clamps the selected index to the chip length
    if (index >= currentChips.length) index = currentChips.length - 1;

    handleDeleteChip(currentChips[index].id);
    // At this point the chip deletion cannot be seen in the `chips` array (it updates in the next render)
    // therefore a `chips` array with 1 item will be empty on the next render
    if (currentChips.length <= 1) {
      setSelectedChipIndex(-1);
      return;
    }

    const nextChipIndex = clamp(index - 1, 0);
    const firstChip = index === 0;

    // Since the chip deletion is not detected yet, if the first chip is deleted then the next
    // chip to be selected is at index 1 not 0
    chipsRef.current[firstChip ? 1 : nextChipIndex]?.focus();

    setSelectedChipIndex(nextChipIndex);
  }

  // Selects a chip at the specified offset from the currently selected one
  function moveSelectedChip(amount: number) {
    let newIndex = selectedChipIndex;

    // If no chip is selected
    if (newIndex === -1) {
      newIndex = currentChips.length;
    }

    newIndex += amount;
    newIndex = clamp(newIndex, 0, currentChips.length - 1);

    chipsRef.current[newIndex]?.focus();

    setSelectedChipIndex(newIndex);
  }

  async function onFocusLost(e: React.FocusEvent<HTMLInputElement>) {
    setFocused(false);

    createChips(inputValue);

    // in case we have another blur callback
    onBlur?.(e);
  }
};

export const ChipHolder = forwardRef(ChipHolderRefForwardingComponent);

export default ChipHolder;
